import { useUpdateUser } from '@/hooks';
import { phoneNumberSchema } from '@/schema';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { Button, Dialog, Stack, Typography } from '@prenuvo/halo-web';
import { produce } from 'immer';
import { Dispatch, SetStateAction, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import PhoneInputField from '../MemberInfoForm/PhoneInputField';

interface updatedPhoneNumber {
  error: string | undefined;
  value: string | undefined;
  serverError: boolean;
}

interface UpdatePhoneNumberDialogProps {
  onUpdate: Dispatch<SetStateAction<boolean>>;
  dialogStatus: boolean;
}

const UpdatePhoneNumberDialog = ({ onUpdate, dialogStatus }: UpdatePhoneNumberDialogProps) => {
  const { setMemberInfo, getMemberInfo, clearErrors } = usePrenuvoStore(
    useShallow((state) => ({
      setMemberInfo: state.user.methods.setMemberInfo,
      getMemberInfo: state.user.methods.getMemberInfo,
      clearErrors: state.user.methods.clearErrors,
    })),
  );

  const { trigger: triggerUpdateUser } = useUpdateUser();

  const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState<updatedPhoneNumber>({
    error: undefined,
    value: undefined,
    serverError: false,
  });

  const onUpdateHandler = async () => {
    clearErrors();

    if (updatedPhoneNumber?.error || !updatedPhoneNumber?.value) {
      setUpdatedPhoneNumber(
        produce(updatedPhoneNumber, (draft) => {
          draft.error = updatedPhoneNumber?.error || 'Phone number is required.';
        }),
      );
      return;
    }

    const { userId } = getMemberInfo();

    const result = await triggerUpdateUser({
      userId,
      user: {
        phoneNumber: updatedPhoneNumber?.value,
      },
    });

    if (!result?.data?.updateUser?.user) {
      setUpdatedPhoneNumber(
        produce(updatedPhoneNumber, (draft) => {
          draft.error = 'Something went wrong. Please try again later.';
          draft.serverError = true;
        }),
      );
      return;
    }

    setMemberInfo({ phoneNumber: updatedPhoneNumber?.value });
    onUpdate(false);
  };

  const onChangeHandler = (filed: string, value: any) => {
    if (filed !== 'updatedPhoneNumber') return;
    const result = phoneNumberSchema.safeParse({
      phoneNumber: value,
    });

    setUpdatedPhoneNumber(
      produce(updatedPhoneNumber, (draft) => {
        draft.error = result?.error?.issues[0]?.message;
        draft.value = value;
        draft.serverError = false;
      }),
    );
  };

  return (
    <Dialog
      title={{ children: 'Please update your phone number!' }}
      open={dialogStatus}
      footer={
        <Stack gap={4} justification="end">
          {updatedPhoneNumber?.serverError && (
            <Button variant="text" onClick={() => onUpdate(false)}>
              Close
            </Button>
          )}
          <Button variant="primary" onClick={onUpdateHandler}>
            <span className="px-6">Update</span>
          </Button>
        </Stack>
      }
    >
      <Stack gap={2} variant="vertical">
        <Typography variant="paragraph-md" className="mb-2">
          We&apos;ve noticed that the phone number we have on file for you isn&apos;t valid. This
          may be due to a change in our support for international phone numbers. Please provide us
          with a valid number to contact you at.
        </Typography>
        <PhoneInputField
          value={updatedPhoneNumber?.value}
          onChange={onChangeHandler}
          error={updatedPhoneNumber?.error}
          placeholder="Phone number"
          name="updatedPhoneNumber"
        />
      </Stack>
    </Dialog>
  );
};

export default UpdatePhoneNumberDialog;
